import type { UserProfile } from '@/app/types/auth';

/**
 * Roles the backend accepts on POST /sales-orders/ (and on the /items/,
 * /item-prices/ and /categories/ reads the order builder depends on).
 *
 * `require_role` in backend/src/api/dependencies.py is a flat match on
 * `user.role.name`, with superusers bypassing the check entirely.
 */
const SALES_ORDER_ROLES = ['org_admin', 'data_entry'];

/**
 * Whether this user can create sales orders.
 *
 * `field_agent` accounts sign into this PWA for notifications but are excluded from
 * the parties, items and payments endpoints, so gate the entry points rather than
 * letting them walk into a 403.
 */
export const canCreateSalesOrders = (user: UserProfile | null | undefined): boolean => {
    if (!user) return false;
    if (user.is_superuser) return true;
    return !!user.role?.name && SALES_ORDER_ROLES.includes(user.role.name);
};

/**
 * Roles the backend accepts on the /leads endpoints (ROLES_RW in
 * backend/src/api/v1/endpoints/leads.py). `client` is absent on both sides —
 * customer-portal users must never see the prospect pipeline.
 */
const LEAD_ROLES = ['org_admin', 'standard_user', 'data_entry'];

export const canManageLeads = (user: UserProfile | null | undefined): boolean => {
    if (!user) return false;
    if (user.is_superuser) return true;
    return !!user.role?.name && LEAD_ROLES.includes(user.role.name);
};

/**
 * Whether this user can create customer app logins and reset their PINs.
 *
 * Granting app access is a controlled action: the create/reset endpoints
 * (`POST /parties/{id}/create-client-user`, `POST /users/{id}/reset-pin`) are
 * org_admin-only on the backend, and sales managers hold that role.
 */
export const canManageClientAccess = (user: UserProfile | null | undefined): boolean => {
    if (!user) return false;
    if (user.is_superuser) return true;
    return user.role?.name === 'org_admin';
};
